import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ActiveCartFacade, OrderEntry } from '@spartacus/cart/base/root';
import {
  AuthService,
  BaseSite,
  EventService,
  LoginEvent,
  LogoutEvent,
  Price,
  RouterState,
  RoutingService,
  User,
  WindowRef
} from '@spartacus/core';
import { UserAccountService } from '@spartacus/user/account/core';
import Cookies from 'js-cookie';
import { combineLatest, from, Observable, of } from 'rxjs';
import { filter, find, map, pairwise, switchMap, take, tap } from 'rxjs/operators';
import { Md5 } from 'ts-md5';

import { DpFallbackLanguageService } from '@app/core/services/fallback-language.service';
import { DpProductsService } from '@app/core/services/products.service';
import { CustomerPrice, CustomerPriceError } from '@app/core/types/backend/customer-price.type';
import { DpOccProduct } from '@app/core/types/backend/dp-product.interface';
import { DpGtmTagProduct } from '@app/core/types/common/dp-gtm-tag-product.interface';
import { DpGtmTag } from '@app/core/types/common/dp-gtm-tag.interface';
import { ECommerceProps } from '@app/core/types/common/e-commerce-props.type';
import { DpOrderEntry } from '@app/core/types/view/dp-order-entry.interface';
import { DpProduct } from '@app/core/types/view/dp-product.interface';
import { ProductsCategory } from '@app/core/types/view/products-category.type';
import { DpMarketSelectorService } from '@app-modules/site-context/services/market-selector.service';
import { DpGoogleTagManagerService, GoogleAnalyticsItem } from '@app-shared/google-tag-manager';

@Injectable({ providedIn: 'root' })
export class DpGtmService {
  previousSemanticRoute: string | undefined;
  currentSemanticRoute: string | undefined;
  promotion: Partial<GoogleAnalyticsItem> = {};
  categories: Partial<GoogleAnalyticsItem> = {};
  initPushed = false;

  private readonly routeMap: Map<string, () => Observable<void>>;
  readonly #destroyRef = inject(DestroyRef);
  readonly #fallbackLanguage = inject(DpFallbackLanguageService).fallbackLanguage;
  readonly #isLoggedIn$ = inject(AuthService).isUserLoggedIn();

  constructor(
    private readonly dpProductsService: DpProductsService,
    private readonly googleTagManagerService: DpGoogleTagManagerService,
    private readonly routingService: RoutingService,
    private readonly activeCartService: ActiveCartFacade,
    private readonly eventService: EventService,
    private readonly dpMarketSelectorService: DpMarketSelectorService,
    private readonly winRef: WindowRef,
    private readonly userService: UserAccountService
  ) {
    this.routeMap = new Map<string, () => Observable<void>>()
      .set('cart', () => this.pushCheckoutTag(1))
      .set('checkoutPaymentType', () => this.pushCheckoutTag(2))
      .set('checkoutDeliveryAddress', () => this.pushCheckoutTag(3))
      .set('checkoutReviewOrder', () => this.pushCheckoutTag(4))
      .set('product', () => this.pushProductTag());
  }

  /**
   * Listens to route change
   * Is used for legacy checkout google tag pushes.
   */
  initializeListener(): Observable<RouterState> {
    this.googleTagManagerService.addGtmToDom();
    return this.routingService.getRouterState().pipe(
      pairwise(),
      filter(([previous, current]) => current.state.url === previous.nextState?.url),
      map(([_, current]) => current),
      filter((event) => event.nextState === undefined),
      tap((routerState) => {
        this.previousSemanticRoute = this.currentSemanticRoute;
        this.currentSemanticRoute = routerState.state?.semanticRoute;

        if (this.currentSemanticRoute && this.routeMap.has(this.currentSemanticRoute)) {
          const routeFunction = this.routeMap.get(this.currentSemanticRoute);

          if (routeFunction) {
            routeFunction().pipe(takeUntilDestroyed(this.#destroyRef)).subscribe();
          }
        }
      })
    );
  }

  /**
   * Saves user country, market and lang to cookie.
   * Sends login and init google tag.
   */
  loginListener(): Observable<BaseSite | undefined> {
    return this.eventService.get(LoginEvent).pipe(
      switchMap(() => this.dpMarketSelectorService.getUserAuthMarketAndCountry()),
      switchMap((user: User) => {
        this.dpMarketSelectorService.setMarketAndCountryCookie(user?.country);
        this.pushLoginGTMTag();
        void this.initGtmPush(user);

        return this.dpMarketSelectorService.setCurrent(user?.market, user?.country, user?.language?.isocode);
      }),
      take(1)
    );
  }

  /**
   * Sends updated init google tag on logout
   */
  logoutListener(): void {
    this.eventService
      .get(LogoutEvent)
      .pipe(take(1))
      .subscribe(() => {
        this.initGtmPush();
      });
  }

  pushLoginGTMTag(): void {
    this.pushSimpleTag('formSubmit', { formType: 'Login' });
  }

  /**
   * Sends init google tag.
   * Used in base site initialiser, other places use initGtmPush
   * @param isLoggedIn is used to get the user data
   */
  handleGtmInit(isLoggedIn: boolean): void {
    if (this.initPushed) {
      return;
    }

    if (isLoggedIn) {
      this.initPushed = true;
      this.userService
        .get()
        .pipe(find((user) => !!user))
        .subscribe((user) => {
          const dpUser = user;

          this.initGtmPush(dpUser as User).then();
        });
    } else {
      this.initPushed = true;
      this.initGtmPush().then();
    }
  }

  /**
   * Sends init google tag.
   * @param user if provided user data is sent
   */
  initGtmPush(user?: User): Promise<void> {
    const site_country: string = Cookies.get('country') ?? 'uk-ie';
    const site_language: string = Cookies.get('language') ?? this.#fallbackLanguage ?? 'en';
    const encrypt = (text: string | undefined): string => (text ? Md5.hashStr(text) : '');

    return this.googleTagManagerService.pushTag({
      event: 'init',
      logged_in: !!user,
      site_language,
      site_country,
      ...(user && {
        userData: {
          user_id: user.customerId,
          customer_id: user.erpId,
          user_email: user.uid,
          user_phone: user.defaultAddress?.phone,
          user_firstname: user.firstName?.toLowerCase(),
          user_lastname: user.lastName?.toLowerCase(),
          ...(user?.titleCode && { user_gender: user.titleCode === 'mr' ? 'm' : 'f' }),
          user_address: user.defaultAddress?.line1,
          user_city_fb: user.defaultAddress?.town?.replace(' ', '').toLowerCase(),
          user_city_gads: user.defaultAddress?.town,
          user_region: user.defaultAddress?.region?.isocodeShort,
          user_cap: user.defaultAddress?.postalCode,
          user_country: user.country?.isocode?.toLowerCase(),
          user_email_hash: encrypt(user.uid),
          user_phone_hash: encrypt(user.defaultAddress?.phone),
          user_firstname_hash: encrypt(user.firstName?.toLowerCase()),
          user_lastname_hash: encrypt(user.lastName?.toLowerCase()),
          user_gender_hash: encrypt(user.titleCode === 'mr' ? 'm' : 'f'),
          user_address_hash: encrypt(user.defaultAddress?.line1),
          user_city_fb_hash: encrypt(user.defaultAddress?.town?.replace(' ', '').toLowerCase()),
          user_city_gads_hash: encrypt(user.defaultAddress?.town),
          user_region_hash: encrypt(user.defaultAddress?.region?.isocodeShort),
          user_cap_hash: encrypt(user.defaultAddress?.postalCode),
          user_country_hash: encrypt(user.country?.isocode?.toLowerCase())
        }
      })
    });
  }

  /**
   *  Generates data for GTM checkout push.
   *  @param event name for GTM event
   *  @param ecommerceProps additional props for ecommerce prop
   *  @param hasPaymentType whether paymentType should be added to props
   */

  pushCheckoutTagV2(event: string, ecommerceProps: ECommerceProps = {}, hasPaymentType = false): void {
    this.activeCartService
      .getActive()
      .pipe(take(1))
      .subscribe((cart) => {
        void this.pushTag(
          event,
          (cart.entries ?? []).reduce((acc: GoogleAnalyticsItem[], entry) => {
            acc.push({
              item_id: entry.product?.code,
              item_name: entry.product?.name,
              currency: entry.basePrice?.currencyIso,
              item_brand: entry.product?.brandName,
              price: entry.basePrice?.value,
              quantity: entry.quantity
            });

            return acc;
          }, []),
          {
            ...(hasPaymentType && { payment_type: cart.paymentType?.code }),
            ...ecommerceProps,
            currency: cart.totalPrice?.currencyIso,
            value: cart.totalPrice?.value
          },
          cart.code
        );
      });
  }

  pushAddToCartTag(event: string, items: GoogleAnalyticsItem[], cartCode: string): Promise<void> {
    this.saveCategoriesToLocalStorage(items[0].item_id, this.categories, cartCode);

    return this.googleTagManagerService.pushTag({
      event,
      ecommerce: {
        items: items.map((item) => ({
          ...item,
          ...this.categories
        }))
      }
    });
  }

  selectProductFromList(
    product: DpProduct,
    category: ProductsCategory,
    price: Price,
    index: number,
    list: string
  ): void {
    this.googleTagManagerService.pushTag({
      event: 'eec-productClick',
      ecommerce: {
        currencyCode: price.currencyIso,
        add: {
          actionField: { list },
          products: [
            {
              name: product.name,
              id: product.code,
              price: price.value,
              brand: product.brandName,
              category: category.name
            }
          ]
        }
      }
    });

    this.setCategories(product.url, category?.code, 'category');
    this.pushTag('select_item', [
      {
        item_id: product.code,
        item_name: product.name,
        currency: price.currencyIso,
        index,
        item_brand: product.brandName,
        price: price.value,
        quantity: 1
      }
    ]);
  }

  trackProducts(
    categoryCode?: string,
    productUrl?: string,
    productsTracked: string[] = [],
    productsToTrack: DpProduct[] = []
  ): void {
    this.setCategories(productUrl, categoryCode, 'category');
    const gtmProductArray: { [key: string]: string | number }[] = [];
    const items = (productsToTrack ?? []).reduce((acc: GoogleAnalyticsItem[], product, index) => {
      if (product.code && !productsTracked.includes(product.code)) {
        productsTracked.push(product.code);
        gtmProductArray.push({
          name: product.name ?? '',
          id: product.code,
          price: 0,
          brand: product.brandName ?? '',
          category: categoryCode ?? '',
          list: 'Category'
        });
        acc.push({
          item_id: product.code,
          item_name: product.name,
          index,
          item_brand: product.brandName,
          quantity: 1
        });
      }

      return acc;
    }, []);

    if (items.length) {
      this.pushTag('view_item_list', items);
    }

    if (gtmProductArray.length) {
      void this.googleTagManagerService.pushTag({
        event: 'eec-productView',
        ecommerce: {
          impressions: gtmProductArray
        }
      });
    }
  }

  /**
   *  Generic GTM push.
   *  @param event name for GTM event
   *  @param items populate items prop within ecommerce
   *  @param ecommerceProps additional props for ecommerce prop
   *  @param cartCode cart code
   */

  pushTag(event: string, items: GoogleAnalyticsItem[], ecommerceProps = {}, cartCode = ''): Promise<void> {
    return this.googleTagManagerService.pushTag({
      event,
      ecommerce: {
        ...ecommerceProps,
        items: items.map((item) => ({
          ...item,
          currency: item.currency ?? Cookies.get('dormer_pramet_currency'),
          ...(cartCode ? this.setCategoriesFromStorage(item.item_id, cartCode) : this.categories),
          ...this.promotion
        }))
      }
    });
  }

  /**
   *  Saves categories related to product to localStorage.
   *  We need it there so that we can assign right categories to products
   *  in the cart - which can be view after some time.
   *  @param code product code
   *  @param categories categories
   *  @param cartCode cart code
   */
  saveCategoriesToLocalStorage(code = '', categories: Partial<GoogleAnalyticsItem>, cartCode: string): void {
    if (code) {
      localStorage.setItem('dp_item_' + code + '_' + cartCode, JSON.stringify({ ...categories, ...this.promotion }));
    }
  }

  /**
   *  Deletes product GA categories from localStorage.
   *  @param productCode product code
   */
  eraseCategoriesFromLocalStorage(productCode: string | undefined): void {
    if (productCode) {
      this.activeCartService
        .getActive()
        .pipe(take(1))
        .subscribe(({ code }) => localStorage.removeItem('dp_item_' + productCode + '_' + code));
    }
  }

  /**
   *  Gets product categories from localStorage
   *  @param code productCode
   *  @param cartCode cart code
   *  @returns categories either categories or empty object
   */
  setCategoriesFromStorage(code = '', cartCode: string): Partial<GoogleAnalyticsItem> {
    const item = localStorage.getItem('dp_item_' + code + '_' + cartCode);

    return item ? JSON.parse(item) : {};
  }

  /**
   *  GTM push without items.
   *  Also set promotion if condition is met.
   *  @param event name for GTM event
   *  @param props additional
   */
  pushSimpleTag(event: string, props = {}): Promise<void> {
    if ('promotion_id' in props && event === 'select_promotion') {
      this.promotion = props as Partial<GoogleAnalyticsItem>;
    }

    return this.googleTagManagerService.pushTag({
      event,
      ...props
    });
  }

  /**
   *  Sets categories from wexUrl.
   *  @return categories
   */
  setCategories(wexUrl = '', code = '', item_list_id?: string): void {
    const urlParts = wexUrl.split('/').slice(3, 6);

    this.categories = {
      item_category: code,
      item_category2: urlParts[0],
      item_category3: urlParts[1],
      item_category4: urlParts[2],
      item_list_name: urlParts.join('-'),
      item_list_id,
      ...this.promotion
    };
  }

  /**
   * Sends checkout google tag.
   * Legacy google tag - events always include eec
   * @param checkoutStep
   */
  pushCheckoutTag(checkoutStep: number): Observable<void> {
    return this.activeCartService.getEntries().pipe(
      take(1),
      filter((entries: OrderEntry[]): entries is DpOrderEntry[] => entries.length > 0),
      switchMap((entries) => {
        const tag: DpGtmTag = {
          event: 'eec-checkout',
          ecommerce: {
            checkout: {
              actionField: { step: checkoutStep },
              products: []
            }
          }
        };

        entries.forEach((entry) => {
          const product: DpGtmTagProduct = {
            name: entry.product?.name,
            id: entry.product?.code,
            price: entry.basePrice?.value,
            brand: entry.product?.brandName,
            category: entry.product?.categories?.[0]?.name ?? entry.product?.categories?.[0]?.code,
            quantity: entry.quantity
          };

          tag.ecommerce.checkout?.products.push(product);
        });

        return from(this.googleTagManagerService.pushTag(tag));
      })
    );
  }

  private pushProductTag(): Observable<void> {
    this.dpProductsService.setPriceResponse({} as CustomerPriceError);

    if (!this.winRef.isBrowser()) {
      return of();
    }

    return this.#isLoggedIn$.pipe(
      take(1),
      switchMap((isLoggedIn) => this.#getProductWithPrice(isLoggedIn)),
      switchMap(({ product, price }) => this.#pushProductTag(product, price))
    );
  }

  #getProductWithPrice(isLoggedIn: boolean): Observable<{ product: DpOccProduct; price?: number }> {
    const product$ = this.dpProductsService.getProduct().pipe(take(1));

    if (!isLoggedIn) {
      return product$.pipe(map((product) => ({ product })));
    }

    const price$ = this.dpProductsService.fetchPrice().pipe(
      take(1),
      map((price) => (price as CustomerPrice)?.customerPrice?.value)
    );

    return combineLatest([product$, price$]).pipe(map(([product, price]) => ({ product, price })));
  }

  #pushProductTag(product: DpOccProduct, gtmPrice = 0): Observable<void> {
    const tag: DpGtmTag = {
      event: 'eec-productDetail',
      ecommerce: {
        detail: {
          actionField: {
            list: this.previousSemanticRoute ?? 'url'
          },
          products: [
            {
              name: product.name,
              id: product.code,
              price: gtmPrice,
              brand: product.brandName,
              category: product.categories?.[0]?.name
            }
          ]
        }
      }
    };

    return from(this.googleTagManagerService.pushTag(tag));
  }
}
